import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './QuestionsPage.css';
import { useSearchParams } from 'react-router-dom';

function QuestionsPage() {
  const [questions, setQuestions] = useState([]);
  const [viewMode, setViewMode] = useState('all'); // 'toAnswer', 'myQuestions', ou 'all'
  const [subjectFilter, setSubjectFilter] = useState('');
  const [idFilter, setIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending'); // 'all', 'answered', 'pending'
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [answer, setAnswer] = useState('');
  const navigate = useNavigate();
  const { API_BASE_URL, user } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchQuestions();
    const idParam = searchParams.get('id');
    if (idParam) {
      setIdFilter(idParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}questions`, {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => {
        // Ordenar as questões: primeiro as sem resposta, depois em ordem decrescente de data
        const sortedData = sortQuestions(data);
        setQuestions(sortedData);
      })
      .catch((err) => console.error('Error fetching questions:', err));
  };

  // Função para ordenar as questões
  const sortQuestions = (questionsData) => {
    return [...questionsData].sort((a, b) => {
      // Verifica se as questões têm o campo answeredAt
      const aHasAnswer = a.question.answeredAt;
      const bHasAnswer = b.question.answeredAt;

      // Primeiro critério: questões sem resposta vêm primeiro
      if (!aHasAnswer && bHasAnswer) return -1;
      if (aHasAnswer && !bHasAnswer) return 1;

      // Se ambas têm resposta ou ambas não têm, ordena por data decrescente
      // Assumindo que existe um campo date que pode ser usado para ordenação
      const aDate = a.question.answeredAt || a.question.date;
      const bDate = b.question.answeredAt || b.question.date;

      // Ordenação decrescente (mais recente primeiro)
      if (aDate > bDate) return -1;
      if (aDate < bDate) return 1;

      return 0;
    });
  };

  const subjects = [...new Set(questions.map(({ question }) => question.subject))];

  const filteredIds = subjectFilter
    ? [...new Set(questions.filter(({ question }) => question.subject === subjectFilter).map(({ question }) => question.id.toString()))]
    : [];

  const filteredQuestions = questions.filter((item) => {
    const { question } = item;

    // Primeiro, filtrar por modo de visualização (se não for 'all')
    if (viewMode !== 'all') {
      const isFromCIDSS = !question.questionedBy || question.questionedBy === 'admin';

      if (viewMode === 'toAnswer' && !isFromCIDSS) {
        return false;
      }

      if (viewMode === 'myQuestions' && isFromCIDSS) {
        return false;
      }
    }

    // Aplicar filtro de assunto
    if (subjectFilter && question.subject !== subjectFilter) {
      return false;
    }

    // Aplicar filtro de ID
    if (idFilter && question.id.toString() !== idFilter) {
      return false;
    }

    // Aplicar filtro de status
    const isAnswered = question.answer && question.answer.trim() !== '';

    if (statusFilter === 'answered' && !isAnswered) {
      return false;
    }

    if (statusFilter === 'pending' && isAnswered) {
      return false;
    }

    return true;
  });

  const convertUrlsToLinks = (text) => {
    if (!text) return '';

    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    const htmlText = text.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });

    return { __html: htmlText };
  };

  const submitAnswer = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}questions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({ questionId: selectedQuestionId, answer }),
    })
      .then((res) => res.json())
      .then(() => {
        alert('Resposta enviada com sucesso!');
        setSelectedQuestion(null);
        setSelectedQuestionId(null);
        setAnswer('');
        // Refresh questions after submitting an answer
        fetchQuestions();
      })
      .catch((err) => console.error('Error submitting answer:', err));
  };

  const resetFilters = () => {
    setSubjectFilter('');
    setIdFilter('');
    setStatusFilter('all');
  };

  // Contador de estatísticas
  const statsToAnswer = questions.filter(item => {
    const isFromCIDSS = !item.question.questionedBy || item.question.questionedBy === 'admin';
    const isAnswered = item.question.answer && item.question.answer.trim() !== '';
    return isFromCIDSS && !isAnswered;
  }).length;

  const statsMyQuestionsAnswered = questions.filter(item => {
    const isFromCIDSS = !item.question.questionedBy || item.question.questionedBy === 'admin';
    const isAnswered = item.question.answer && item.question.answer.trim() !== '';
    return !isFromCIDSS && isAnswered;
  }).length;

  const statsMyQuestionsTotal = questions.filter(item => {
    const isFromCIDSS = !item.question.questionedBy || item.question.questionedBy === 'admin';
    return !isFromCIDSS;
  }).length;

  const statsTotalAnswered = questions.filter(item => {
    return item.question.answer && item.question.answer.trim() !== '';
  }).length;

  // Determinar se podemos responder à pergunta selecionada
  const canAnswerSelected = selectedQuestion &&
    (!selectedQuestion.questionedBy || selectedQuestion.questionedBy === 'admin');

  return (
    <div className="questions-container">
      <h2 className="title">Question Center</h2>

      <div className="view-tabs">
        <button
          className={`view-tab ${viewMode === 'all' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('all');
            resetFilters();
          }}
        >
          All Questions
          <span className="badge">{statsTotalAnswered}/{questions.length}</span>
        </button>
        <button
          className={`view-tab ${viewMode === 'toAnswer' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('toAnswer');
            resetFilters();
          }}
        >
          Questions to Answer
          <span className="badge">{statsToAnswer}</span>
        </button>
        <button
          className={`view-tab ${viewMode === 'myQuestions' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('myQuestions');
            resetFilters();
          }}
        >
          My Questions
          <span className="badge">{statsMyQuestionsAnswered}/{statsMyQuestionsTotal}</span>
        </button>
      </div>

      <div className="filters">
        <div className="filters-row">
          <select
            className="filter-dropdown"
            value={subjectFilter}
            onChange={(e) => {
              setSubjectFilter(e.target.value);
              setIdFilter('');
            }}
          >
            <option value="">Filter by subject</option>
            {subjects.map((subject) => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>

          <select
            className="filter-dropdown"
            value={idFilter}
            onChange={(e) => setIdFilter(e.target.value)}
            disabled={!subjectFilter}
          >
            <option value="">Filtrar por ID</option>
            {filteredIds.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
        </div>

        <div className="filters-row">
          <div className="status-buttons">
            <button
              className={`status-button ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All
            </button>
            <button
              className={`status-button ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter('pending')}
            >
              Pending
            </button>
            <button
              className={`status-button ${statusFilter === 'answered' ? 'active' : ''}`}
              onClick={() => setStatusFilter('answered')}
            >
              Answered
            </button>
          </div>

          <button
            className="reset-button"
            onClick={resetFilters}
          >
            Clean Filters
          </button>
        </div>
      </div>

      <div className="questions-list">
        {filteredQuestions.length > 0 ? (
          filteredQuestions.map(({ id, question }) => {
            const isFromCIDSS = !question.questionedBy || question.questionedBy === 'admin';
            return (
              <div
                key={id}
                className={`question-card ${question.answer ? 'answered' : 'pending'} ${isFromCIDSS ? 'from-cidss' : 'from-me'}`}
                onClick={() => {
                  setSelectedQuestion({ ...question, existingAnswer: question.answer });
                  setSelectedQuestionId(id);
                  if (question.answer) setAnswer(question.answer);
                }}
              >
                <div className="card-header">
                  <span className="question-id">{question.id}</span>
                  <span className="question-subject">{question.subject}</span>
                  <span className={`status-badge ${question.answer ? 'answered' : 'pending'}`}>
                    {question.answer ? 'Answered' : 'Pending'}
                  </span>
                </div>

                <p className="question-text">{question.question}</p>

                <div className="card-footer">
                  {question.questionedBy && question.questionedBy !== 'admin' ? (
                    <span className="question-by">Asked by {question.questionedBy}</span>
                  ) : (
                    <span className="question-by">Asked by admin</span>
                  )}
                  <span className="question-date">{question.date || 'No date available'}</span>
                </div>

                {question.answer && (
                  <div className="preview-answer">
                    <span className="question-by">Answered by {question.answeredBy}</span>
                    <p><strong>Answer:</strong> {question.answer.substring(0, 100)}{question.answer.length > 100 ? '...' : ''}</p>
                  </div>
                )}

                {viewMode === 'all' && (
                  <div className="card-tag">
                    {isFromCIDSS ? 'Question' : 'Client Question'}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="no-questions">
            {viewMode === 'toAnswer'
              ? 'No questions matching current filters.'
              : viewMode === 'myQuestions'
                ? 'You still have not made questions or there are no questions matching current filters.'
                : 'No questions matching current filters.'}
          </div>
        )}
      </div>

      {selectedQuestion && (
        <div className="modal">
          <div className="modal-content">
            <button className="close-btn" onClick={() => {
              setSelectedQuestion(null);
              setSelectedQuestionId(null);
              setAnswer('');
            }}>
              &times;
            </button>

            <div className="modal-header">
              <h3>
                {canAnswerSelected && !selectedQuestion.existingAnswer
                  ? 'Answer Question'
                  : 'View Question'}
              </h3>
              <div className="modal-meta">
                <span>ID: {selectedQuestion.id}</span>
                <span>Subject: {selectedQuestion.subject}</span>
              </div>
            </div>

            <div className="question-detail">
              <strong>Question:</strong>
              <div dangerouslySetInnerHTML={convertUrlsToLinks(selectedQuestion.question)} />

              <p className="question-source">
                {selectedQuestion.questionedBy && selectedQuestion.questionedBy !== 'admin'
                  ? `Asked by: ${selectedQuestion.questionedBy}`
                  : 'Asked by: admin'}
              </p>
            </div>

            <div className="answer-section">
              <strong>Resposta:</strong>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Write your answer here..."
                readOnly={!canAnswerSelected}
              />
            </div>

            {canAnswerSelected && (
              <div className="modal-actions">
                <button
                  onClick={submitAnswer}
                  disabled={!answer.trim()}
                >
                  {selectedQuestion.existingAnswer ? 'Update Answer' : 'Submit Answer'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default QuestionsPage;
